/** Product HOOKS */
import { useState, useEffect } from "react";

/** TYPES */
import type {
	Category,
	Product,
	ProductType,
	Service,
	Package,
} from "@/types/product";
import { Discount } from "@/types/discount";

export const fetchServices = async () => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_URL}/product/service`);
		const data = await res.json();

		if (data.success) {
			return data.data as Service[];
		} else {
			throw new Error("Үйлчилгээнүүдийг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Үйлчилгээнүүдийг татаж чадсангүй");
	}
};

export const fetchService = async (serviceId: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/product/service/${serviceId}`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as Service;
		} else {
			throw new Error("Үйлчилгээг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Үйлчилгээг татаж чадсангүй");
	}
};

export interface CategoriesResponse {
	success: boolean;
	data: {
		categories: Category[];
		total?: number;
	};
}

export const fetchCategories = async (parentName?: string) => {
	try {
		const url = parentName
			? `${process.env.REACT_APP_API_URL}/product/categories/${parentName}`
			: `${process.env.REACT_APP_API_URL}/product/categories`;

		const res = await fetch(url);
		const data: CategoriesResponse = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error("Ангилалуудыг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Ангилалуудыг татаж чадсангүй");
	}
};

export interface CategoryResponse {
	success: boolean;
	data: {
		category: Category;
	};
}

export const fetchCategory = async (id: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/product/category/${id}`,
		);
		const data: CategoryResponse = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error("Ангилалыг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Ангилалыг татаж чадсангүй");
	}
};

export interface TypesResponse {
	success: boolean;
	data: {
		types: ProductType[];
	};
}

export const fetchTypes = async () => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_URL}/product/types`);
		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error("Төрлүүдийг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Төрлүүдийг татаж чадсангүй");
	}
};

export interface ProductsResponse {
	success: boolean;
	data: {
		products: Product[];
		total?: number;
	};
}

export interface ProductProps {
	categoryIds?: string[];
	limit?: number;
	page?: number;
	search?: string;
	packageId?: string;
	type?: string;
	applicableTo?: ("INTERNET" | "PACKAGE" | "PRODUCT")[];
	vendorId?: string;
	sortBy?: string;
	sortOrder?: "asc" | "desc";
	tier?: number;
}

export const fetchProducts = async (props: ProductProps) => {
	try {
		const url = new URL(`${process.env.REACT_APP_API_URL}/product`);

		if (props.categoryIds) {
			url.searchParams.append("categories", props.categoryIds.join(","));
		}

		if (props.limit) {
			url.searchParams.append("limit", props.limit.toString());
		}

		if (props.page) {
			url.searchParams.append("page", props.page.toString());
		}

		if (props.search) {
			url.searchParams.append("search", props.search);
		}

		if (props.packageId) {
			url.searchParams.append("packageId", props.packageId);
		}

		if (props.type) {
			url.searchParams.append("type", props.type);
		}

		if (props.applicableTo) {
			url.searchParams.append("applicableTo", props.applicableTo.join(","));
		}

		if (props.vendorId) {
			url.searchParams.append("vendorId", props.vendorId);
		}

		if (props.tier !== undefined && props.tier !== null) {
			url.searchParams.append("tier", props.tier.toString());
		}

		if (props.sortBy) {
			url.searchParams.append("sortBy", props.sortBy);
		}

		if (props.sortOrder) {
			url.searchParams.append("sortOrder", props.sortOrder);
		}

		const res = await fetch(url.toString());
		const data: ProductsResponse = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error("Бүтээгдэхүүнүүдийг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Бүтээгдэхүүнүүдийг татаж чадсангүй");
	}
};

export const fetchSubpackages = async (packageId: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/package/${packageId}/package`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as Package[];
		} else {
			throw new Error("Дэд багцуудыг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Дэд багцуудыг татаж чадсангүй");
	}
};

export interface PackagesProps {
	limit?: number;
	page?: number;
	search?: string;
	parentId?: string;
	packageId?: string;
	priority?: string;
	ids?: string[];
}

export const fetchPackages = async (props: PackagesProps) => {
	try {
		const url = new URL(`${process.env.REACT_APP_API_URL}/product/packages`);

		if (props.limit) {
			url.searchParams.append("limit", props.limit.toString());
		}

		if (props.page) {
			url.searchParams.append("page", props.page.toString());
		}

		if (props.search) {
			url.searchParams.append("search", props.search);
		}

		if (props.parentId) {
			url.searchParams.append("parentId", props.parentId);
		}

		if (props.packageId) {
			url.searchParams.append("packageId", props.packageId);
		}

		if (props.priority) {
			url.searchParams.append("priority", props.priority);
		}

		if (props.ids) {
			url.searchParams.append("ids", props.ids.join(","));
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as {
				packages: Package[];
				total?: number;
			};
		} else {
			throw new Error("Багцуудыг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Багцуудыг татаж чадсангүй");
	}
};

export const fetchPackage = async (packageId: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/product/package/${packageId}`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data.package as Package;
		} else {
			throw new Error("Багцыг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Багцыг татаж чадсангүй");
	}
};

export const fetchPromotions = async (packageId?: string) => {
	try {
		const url = new URL(`${process.env.REACT_APP_API_URL}/product/promotions`);

		if (packageId) {
			url.searchParams.append("packageId", packageId);
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error("Урамшууллыг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Урамшууллыг татаж чадсангүй");
	}
};

export const fetchProduct = async (productId: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/product/${productId}`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data.product as Product;
		} else {
			throw new Error("Бүтээгдэхүүн татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Бүтээгдэхүүн татаж чадсангүй");
	}
};

export const fetchProductImages = async (productId: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/product/${productId}/images`,
		);
		const data = await res.json();

		if (data.success) {
			return {
				images: data.data.images,
				total: data.data.total,
			};
		} else {
			throw new Error("Зурагуудыг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Зурагуудыг татаж чадсангүй");
	}
};

export const fetchProductTypes = async () => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_URL}/product/types`);
		const data: TypesResponse = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error("Төрлүүдийг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Төрлүүдийг татаж чадсангүй");
	}
};

export const calculatePackagePrice = async (
	packageId: string,
	quantity: number,
	discounts: string[],
) => {
	try {
		const url = new URL(`${process.env.REACT_APP_API_URL}/package/calculate`);

		const res = await fetch(url.toString(), {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				packageId,
				quantity,
				discounts,
			}),
		});

		const data = await res.json();

		if (data.success) {
			return data.data as {
				totalAmount: number;
				originalAmount: number;
				amount: number;
				quantity: number;
				discounts: Discount[];
				vat: number;
				vatExcluded: number;
				discountedAmount: number;
			};
		} else {
			throw new Error("Багцын үнэ тооцоологдсонгүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Багцын үнэ тооцоологдсонгүй");
	}
};

export const calculateServicePrice = async (
	serviceId: string,
	contractId: string | null,
	quantity: number,
	discounts: string[],
) => {
	try {
		const url = contractId
			? new URL(
					`${process.env.REACT_APP_API_URL}/contract/${contractId}/service/calculate`,
				)
			: new URL(`${process.env.REACT_APP_API_URL}/service/calculate`);

		const res = await fetch(url.toString(), {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				serviceId,
				contractId,
				quantity,
				discounts,
			}),
		});

		const data = await res.json();

		if (data.success) {
			return data.data as {
				totalAmount: number;
				originalAmount: number;
				amount: number;
				quantity: number;
				discounts: Discount[];
				vat: number;
				vatExcluded: number;
				discountedAmount: number;
			};
		} else {
			throw new Error("Үйлчилгээний үнэ тооцоологдсонгүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Үйлчилгээний үнэ тооцоологдсонгүй");
	}
};

export const deleteProduct = async (productId: string, option: "status" | "full") => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/product/${productId}/delete`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ option }),
			}
		);
		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error(data.message || "Бүтээгдэхүүн устгаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Бүтээгдэхүүн устгаж чадсангүй");
	}
};

export const fetchAvailableTiers = async () => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_URL}/product/available-tiers`);
		const data = await res.json();

		if (data.success) {
			return data.data as number[];
		} else {
			throw new Error(data.message || "Tier-үүдийг татаж чадсангүй");
		}
	} catch (error) {
		console.error(error);
		throw new Error("Tier-үүдийг татаж чадсангүй");
	}
};
